import type { OrdersParseTree } from "../parser/types";

/**
 * Grid precision check.
 *
 * Ruling of 12 September 2026: every grid written in an order set is
 * six-figure, at every echelon, including grids describing higher's plan. A
 * four-figure grid in source is an authoring defect.
 *
 * The pronunciation rule in doc 03 section 4.4 is deliberately unchanged, so a
 * four-figure grid that reaches the parser is still spoken correctly, digit by
 * digit. Removing it would let a stray value speak as an ordinary number with
 * no warning, which doc 02 section 4 forbids.
 *
 * This raises the warning instead. It never blocks delivery.
 */
export interface GridPrecisionWarning {
  readonly figures: number;
  readonly message: string;
  readonly nodeId: string;
}

/** Six figures is the standard. Anything else in source is flagged. */
const REQUIRED_FIGURES = 6;
const LABELLED_GRID =
  /\b(?:GR|Grid|grid)\s*[:#-]?\s*(\d{2,5})(?:\s+(\d{2,5}))?\b/g;

function figuresIn(first: string, second: string | undefined): number {
  return first.length + (second?.length ?? 0);
}

export function gridPrecisionWarnings(
  tree: OrdersParseTree,
  documentTitle: string | null,
): readonly GridPrecisionWarning[] {
  const warnings: GridPrecisionWarning[] = [];
  const where = documentTitle ?? "this document";

  const items = tree.paragraphs.flatMap((paragraph) => [
    ...paragraph.items,
    ...paragraph.subparagraphs.flatMap((subparagraph) => subparagraph.items),
  ]);

  for (const item of items) {
    LABELLED_GRID.lastIndex = 0;
    for (const match of item.displayText.matchAll(LABELLED_GRID)) {
      const first = match[1];
      /* v8 ignore next -- the capture group is required by the pattern */
      if (first === undefined) continue;
      const figures = figuresIn(first, match[2]);
      if (figures === REQUIRED_FIGURES) continue;
      warnings.push({
        figures,
        nodeId: item.id,
        message: `${where}: ${item.id} carries a ${String(figures)}-figure grid. Every grid in an order set is six-figure. Delivery is not blocked and the value is still spoken digit by digit.`,
      });
    }
  }

  return warnings;
}
